import { Group, Select, Stack, TextInput } from '@mantine/core'
import { useUncontrolled } from '@mantine/hooks'
import type { Patient, Reference, RelatedPerson } from '@medplum/fhirtypes'
import { referenceDisplay } from '@seen/core/display'
import * as relatedPersons from '@seen/core/models/relatedPersons/index'
import { createReference, getReferenceString } from '@seen/core/references'
import { humanNameToString, officialNameOrFallback } from '@seen/core/utils'
import { compact } from 'lodash-es'
import type { JSX } from 'react'
import { FhirQueryLoader } from '#src/components/core/FhirQueryLoader.tsx'
import { SelectCreatable } from '#src/components/input/SelectCreatable.tsx'
import type { SDRRequesterState } from '#src/components/sdr/create/utils.ts'
import { useFhirSearch } from '#src/hooks/useFhirSearch.ts'

type Props = {
  patient: Patient
  label: string
  required?: boolean
  allowCreation?: boolean

  // input props
  onChange: (value: SDRRequesterState | null) => void
  value?: SDRRequesterState | null
  defaultValue?: SDRRequesterState | null
  error?: any
  onFocus?: any
  onBlur?: any
}

export function SDRRequesterInput({
  patient,
  label,
  required,
  allowCreation = true,

  ...inputProps
}: Props): JSX.Element {
  const patientName = referenceDisplay(createReference(patient))

  const [value, handleChange] = useUncontrolled(inputProps)
  const relatedPersonsQuery = useFhirSearch('RelatedPerson', {
    patient,
  })

  let selectValue: string | undefined
  switch (value?.type) {
    case 'participant':
      selectValue = 'participant'
      break
    case 'relatedPerson':
      selectValue = value.reference
        ? getValueForRelatedPerson(value.reference)
        : undefined
      break
    case 'newRelatedPerson':
      selectValue = 'new'
      break
    case undefined:
      break
  }

  return (
    <FhirQueryLoader query={relatedPersonsQuery}>
      {(relatedPersonsArr) => {
        const relatedPersonsValueToReference: Record<
          string,
          Reference<RelatedPerson>
        > = {}
        relatedPersonsArr.forEach((relatedPerson) => {
          relatedPersonsValueToReference[
            getValueForRelatedPerson(createReference(relatedPerson))
          ] = createReference(relatedPerson)
        })
        return (
          <Stack gap={8}>
            <Select
              label={label}
              placeholder="Select..."
              required={required}
              allowDeselect={false}
              data={compact([
                {
                  value: 'participant' as const,
                  label: patientName
                    ? `${patientName} (Participant)`
                    : 'Participant',
                },
                ...relatedPersonsArr.map((relatedPerson) => {
                  const nameToUse = officialNameOrFallback(relatedPerson)
                  if (nameToUse == null) {
                    return null
                  }

                  let label = humanNameToString(nameToUse)
                  const relationships =
                    relatedPersons.getPersonRelationshipToPatientFromCode(
                      relatedPerson.relationship
                    )
                  if (relationships.length > 0) {
                    label += ` (${relationships.join(', ')})`
                  }
                  return {
                    value: getValueForRelatedPerson(
                      createReference(relatedPerson)
                    ),
                    label,
                  }
                }),
                allowCreation && {
                  value: 'new',
                  label: 'Enter a new person',
                },
              ])}
              {...inputProps}
              defaultValue={
                inputProps.defaultValue != null ? selectValue : undefined
              }
              value={inputProps.value != null ? selectValue : undefined}
              onChange={(value) => {
                if (!value) {
                  return
                }

                if (value === 'participant') {
                  handleChange({
                    type: 'participant',
                  })
                } else if (value === 'new') {
                  handleChange({
                    type: 'newRelatedPerson',
                    firstName: '',
                    lastName: '',
                    phone: '',
                    email: '',
                    relationship: null,

                    streetAddress: '',
                    city: '',
                    state: '',
                    zip: '',
                    country: '',
                  })
                } else {
                  handleChange({
                    type: 'relatedPerson',
                    reference: relatedPersonsValueToReference[value],
                  })
                }
              }}
            />

            {value?.type === 'newRelatedPerson' && (
              <>
                <Group>
                  <TextInput
                    label="First name"
                    required
                    value={value.firstName}
                    onChange={(e) => {
                      handleChange({
                        ...value,
                        firstName: e.currentTarget.value,
                      })
                    }}
                    className="grow"
                  />

                  <TextInput
                    label="Last name"
                    required
                    value={value.lastName}
                    onChange={(e) => {
                      handleChange({
                        ...value,
                        lastName: e.currentTarget.value,
                      })
                    }}
                    className="grow"
                  />

                  <SelectCreatable
                    initialOptions={['Caregiver', 'Family Member']}
                    label="Relationship to patient"
                    required
                    allowDeselect={false}
                    value={value.relationship}
                    onChange={(newValue) => {
                      handleChange({
                        ...value,
                        relationship: newValue,
                      })
                    }}
                    className="grow"
                  />
                </Group>

                <Group>
                  <TextInput
                    label="Email"
                    required
                    value={value.email}
                    onChange={(e) => {
                      handleChange({
                        ...value,
                        email: e.currentTarget.value,
                      })
                    }}
                    className="grow"
                  />

                  <TextInput
                    label="Phone"
                    required
                    value={value.phone}
                    onChange={(e) => {
                      handleChange({
                        ...value,
                        phone: e.currentTarget.value,
                      })
                    }}
                    className="grow"
                  />
                </Group>

                {/* Address */}
                <Group>
                  <TextInput
                    label="Street address"
                    required
                    value={value.streetAddress}
                    onChange={(e) => {
                      handleChange({
                        ...value,
                        streetAddress: e.currentTarget.value,
                      })
                    }}
                    className="grow"
                  />

                  <TextInput
                    label="City"
                    required
                    value={value.city}
                    onChange={(e) => {
                      handleChange({
                        ...value,
                        city: e.currentTarget.value,
                      })
                    }}
                    className="grow"
                  />
                </Group>

                <Group>
                  <TextInput
                    label="State"
                    required
                    value={value.state}
                    onChange={(e) => {
                      handleChange({
                        ...value,
                        state: e.currentTarget.value,
                      })
                    }}
                    className="grow"
                  />

                  <TextInput
                    label="Zip"
                    required
                    value={value.zip}
                    onChange={(e) => {
                      handleChange({
                        ...value,
                        zip: e.currentTarget.value,
                      })
                    }}
                    className="grow"
                  />

                  <TextInput
                    label="Country"
                    required
                    value={value.country}
                    onChange={(e) => {
                      handleChange({
                        ...value,
                        country: e.currentTarget.value,
                      })
                    }}
                    className="grow"
                  />
                </Group>
              </>
            )}
          </Stack>
        )
      }}
    </FhirQueryLoader>
  )
}

const RELATED_PERSON_VALUE_PREFIX = 'relatedPerson-'

function getValueForRelatedPerson(reference: Reference<RelatedPerson>): string {
  return `${RELATED_PERSON_VALUE_PREFIX}${getReferenceString(reference)}`
}
